import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { Text } from "@smartsuite/react-ui/lib";
import { useRootStore } from "../../../../stores/hooks";
import { ActionSectionWrapper, SectionHeading } from "../../style";
import type { CurrSection } from "../../types";
import { ConditionalGroupIcon } from "../../../../components/ActionGroup/ActionGroupIcon";
import { StyledLabel } from "../../../../components/ActionGroup/styles";
import { SelectFindRecords } from "../../Forms/InputForm/RecordsSourceEditor/SelectFindRecords";
import {
  isLoopingOutputActionId,
  isSingleFindRecordsAction,
} from "../../Forms/InputForm/RecordsSourceEditor/utils";
import { SelectWebhookOutput } from "../LoopingSection/components/SelectWebhookOutput";
import { ConditionalGroupNameInput } from "../LoopingSection/components/GroupNameInput";
import {
  StyledWrapperHeader,
  StyledHeaderWrapper,
  SectionHeading as LoopNameHeading,
} from "../LoopingSection/styles";

/**
 * Settings panel for a loop group nested inside a branch (Conditional
 * Logic). Mirrors LoopingSection's own layout and validity logic, but
 * addresses the loop via `instanceId` through LoopingStore's scoped
 * accessors (loopGroupById/sourceAt/setSourceAt/loopNameAt/setLoopNameAt/
 * defaultGroupNameAt) instead of the singleton root loop.
 */
export const NestedLoopSection = observer<{
  instanceId: number;
  setSection: (section: CurrSection) => void;
}>(({ instanceId }) => {
  const store = useRootStore();
  const looping = store.automations.candidate.looping;

  // Keeps data-testid="nested-loop-section" on the root in every case
  // (including an unknown/stale instanceId) -- SectionSwitch's routing test
  // relies on it being present unconditionally, same as BranchSection.
  if (looping.loopGroupById(instanceId) === undefined) {
    return <div data-testid="nested-loop-section" />;
  }

  const source = looping.sourceAt(instanceId);
  const sourceReference =
    source?.oneofKind === "outputReference"
      ? source.outputReference.source
      : source?.oneofKind === "recordList"
        ? source.recordList.recordList
        : undefined;
  const sourceAction =
    sourceReference?.oneofKind === "actionReference"
      ? store.automations.candidate.allActions.find(
          (action) =>
            action.actionReference?.instanceId ===
            sourceReference.actionReference.instanceId,
        )
      : undefined;
  const isSingleFindSource = isSingleFindRecordsAction(sourceAction);

  const isValidSource =
    (source?.oneofKind === "outputReference" &&
      (sourceReference?.oneofKind === "triggerReference" ||
        (sourceReference?.oneofKind === "actionReference" &&
          (isLoopingOutputActionId(sourceReference.actionReference?.actionId) ||
            isSingleFindSource)))) ||
    (source?.oneofKind === "recordList" &&
      sourceReference?.oneofKind === "actionReference" &&
      (isLoopingOutputActionId(sourceReference.actionReference?.actionId) ||
        isSingleFindSource));

  const { groupName, isSourceSet } = looping.defaultGroupNameAt(instanceId);
  const loopName = looping.loopNameAt(instanceId);
  const displayName =
    loopName || (isSourceSet ? groupName : t("general.repeat-for-each-in"));

  return (
    <ActionSectionWrapper data-testid="nested-loop-section" theme={{}}>
      <SectionHeading>{t("automation-form.action-settings")}</SectionHeading>
      <StyledWrapperHeader>
        <ConditionalGroupIcon />
        <StyledHeaderWrapper>
          <LoopNameHeading>
            {displayName}
            {!isSourceSet && (
              <StyledLabel className="action-group-name-empty">
                {t("general.action-group-no-list")}
              </StyledLabel>
            )}
          </LoopNameHeading>
          <Text color="secondary">
            {t("looping.repeact-for-each-description")}
          </Text>
        </StyledHeaderWrapper>
      </StyledWrapperHeader>
      <SelectFindRecords loopInstanceId={instanceId} />
      {isValidSource && <SelectWebhookOutput loopInstanceId={instanceId} />}
      <ConditionalGroupNameInput
        loopInstanceId={instanceId}
        value={loopName}
        onChangeValue={(value) => looping.setLoopNameAt(instanceId, value)}
      />
    </ActionSectionWrapper>
  );
});
NestedLoopSection.displayName = "NestedLoopSection";
